<script setup>
import { computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Spinner from '@/components/ui/Spinner.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconCheckmark from '@/components/icon/IconCheckmark.vue'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'

// "Setting up your site address…" — shown under the site header while castr
// reports the address as not ready (Trello oP0vr33o: Preview right after
// creating a site opened a Cloudflare 530). Three steps (register →
// certificate → route), a "taking longer than expected" variant after
// fifteen minutes and an error variant when the registration failed. The
// polling itself is driven by the shell (`useSiteHostReadiness(..., { poll })`).
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
})
// renders nothing while the address is ready; the parent's margin classes go on the box when it shows
defineOptions({ inheritAttrs: false })

const r = useSiteHostReadiness(() => props.siteId)

const stepState = (i) => (i < r.stepIndex.value ? 'done' : i === r.stepIndex.value ? 'current' : 'todo')
const tone = computed(() => (r.failed.value ? 'error' : r.timedOut.value ? 'warning' : 'info'))
const title = computed(() => {
	if (r.failed.value) return `Your site address ${r.host.value} could not be set up`
	if (r.timedOut.value) return 'Setting up your site address is taking longer than expected'
	return 'Setting up your site address…'
})
</script>

<template>
	<div v-if="r.gated.value" v-bind="$attrs" class="rounded-lg border px-4 py-3" role="status" aria-live="polite" data-testid="site-address-setup"
		:data-phase="r.phase.value" :data-tone="tone"
		:class="tone === 'error' ? 'border-error bg-error-transparent' : 'border-surface-4 bg-surface-2'">
		<div class="flex flex-wrap items-start gap-3">
			<span class="mt-0.5 grid size-5 shrink-0 place-items-center" aria-hidden="true">
				<Spinner v-if="tone !== 'error'" spinner-size="16px" spinner-color="var(--c-main-light)" />
				<span v-else class="size-2.5 rounded-full bg-error"></span>
			</span>
			<div class="min-w-0 flex-1">
				<p class="mb-0.5 text-sm font-medium text-white" data-testid="site-address-setup-title">{{ title }}</p>

				<template v-if="r.failed.value">
					<p class="mb-0 text-tiny text-surface-8" data-testid="site-address-setup-detail">
						{{ r.detail.value || 'The address could not be registered with the hosting provider.' }}
						<template v-if="r.retryPending.value"> Castr retries automatically; this page updates when it goes through.</template>
						<template v-else> Try again, and contact support if it keeps failing.</template>
					</p>
				</template>
				<template v-else>
					<p class="mb-0 text-tiny text-surface-8" data-testid="site-address-setup-message">{{ r.message.value }}</p>
					<ol class="m-0 mt-2 flex list-none flex-wrap items-center gap-x-3 gap-y-1 p-0 text-tiny" data-testid="site-address-setup-steps">
						<li v-for="(step, i) in r.steps" :key="step.id" class="inline-flex items-center gap-1.5" :data-step="step.id" :data-state="stepState(i)"
							:class="stepState(i) === 'current' ? 'text-white' : stepState(i) === 'done' ? 'text-surface-7' : 'text-surface-6'">
							<span class="grid size-3.5 shrink-0 place-items-center rounded-full" aria-hidden="true"
								:class="stepState(i) === 'done' ? 'bg-success-transparent text-success' : stepState(i) === 'current' ? 'bg-main-light' : 'border border-surface-5'">
								<icon-base v-if="stepState(i) === 'done'" :width="9" :height="9" fill="none"><icon-checkmark /></icon-base>
								<span v-else-if="stepState(i) === 'current'" class="size-1.5 animate-pulse rounded-full bg-white"></span>
							</span>
							<span>{{ step.label }}</span>
							<span v-if="i < r.steps.length - 1" class="text-surface-5" aria-hidden="true">→</span>
						</li>
					</ol>
					<p v-if="r.timedOut.value" class="mb-0 mt-2 text-tiny text-surface-8" data-testid="site-address-setup-timeout">
						Checks are paused. Refresh to check again, or contact support if the address is still not reachable after a few more minutes.
						<template v-if="r.detail.value"> Last status: {{ r.detail.value }}</template>
					</p>
				</template>
			</div>
			<FormButton v-if="r.failed.value || r.timedOut.value" type="secondary" size="sm" class="shrink-0" :is-loading="r.loading.value" :disabled="r.loading.value" data-testid="site-address-setup-refresh" @click="r.retry()">
				Refresh
			</FormButton>
		</div>
	</div>
</template>
